import { useToast } from '@chakra-ui/react';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { PagePaths } from '@revelio/core';
import { ReportTypeEnum } from '@revelio/data-access';

import { DeliverableStepContainer } from '../../../../deliverables/deliverable-step-container';
import { useGlobalAssistantReportLaunchPendingStore } from '../../../../global-ai-assistant/global-ai-assistant-report-launch-pending';
import { useGlobalAssistantMwReportSkillPrefill } from '../../../../global-ai-assistant/use-global-ai-assistant-mw-report-skill-prefill';
import { useReportBuilderConfig } from '../../../hooks/useReportBuilderConfig';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../../../utils/report-builder-paths';
import { useAllReports } from '../../report-builder.repository';
import { ReportBuilderDeliverablePaths } from '../../report-builder-page';
import { ParametersSectionTitle } from '../parameters-section-title';
import EditEntityValues from './edit-entity-values';
import { useReportsCustomSkills } from './report-entity-selections/useReportsCustomSkills';
import { isHiddenColumn } from './report-hidden-columns';
import { getMatchingEntityPairs, type ReportEntityConfigColumn } from './utils';

const MwEntityConfigurationContent = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const reportBuilderDeliverables = useAllReports();
  const reportEntityMap = useReportBuilderConfig().config;

  const report = Object.values(reportBuilderDeliverables)?.[0];
  const { lists: customSkillLists, isLoading: customSkillListsLoading } =
    useReportsCustomSkills(0);

  useGlobalAssistantMwReportSkillPrefill({
    reportId: report?.id,
    reportType: report?.reportType,
    reportEntityMap,
    customSkillLists,
    customSkillListsLoading,
  });

  const onSubmit = () => {
    const requiredColumns: ReportEntityConfigColumn[] =
      // @ts-expect-error noUncheckedIndexedAccess
      reportEntityMap[report?.reportType].columns
        ?.filter((column: ReportEntityConfigColumn) => !isHiddenColumn(column))
        ?.filter(
          (reportEntity: ReportEntityConfigColumn) => reportEntity.isRequired
        ) ?? [];

    const selectedRequiredColumnsCount = requiredColumns.filter(
      (column) => report?.selections?.[column.id]
    ).length;

    const hasUnselectedMandatoryColumns =
      requiredColumns.length > selectedRequiredColumnsCount;
    const submitValidation = (() => {
      if (hasUnselectedMandatoryColumns) {
        const requiredLeft =
          requiredColumns.length - selectedRequiredColumnsCount;
        return `Please select ${requiredLeft} more mandatory ${
          requiredLeft === 1 ? 'item' : 'items'
        }`;
      }

      const shouldHaveDistinctSelections =
        report?.reportType !== ReportTypeEnum.JobComparison;
      const matchingPairs = getMatchingEntityPairs(
        report?.selections,
        requiredColumns
      );
      if (matchingPairs.length > 0 && shouldHaveDistinctSelections) {
        return `Selections must be distinct for: ${matchingPairs.join(', ')}`;
      }

      return undefined;
    })();

    if (submitValidation) {
      toast({
        title: submitValidation,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    navigate(
      `/${PagePaths.REPORT_BUILDER}/${ReportBuilderDeliverablePaths.CONFIRMATION}`
    );
  };

  const shouldRedirect =
    !report || !reportEntityMap || Object.keys(reportEntityMap).length === 0;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(
        `/${PagePaths.REPORT_BUILDER}/${ReportBuilderDeliverablePaths.REPORT_TYPE}`
      );
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }
  return (
    <DeliverableStepContainer
      id="report_builder_entity"
      title={`Select ${get(
        reportEntityMap[report?.reportType ?? ''] ?? {},
        'label',
        'Report'
      )} Parameters`}
      previousStepPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      onSubmit={onSubmit}
      isSubmitDisabled={false}
      submitButtonContent="Continue"
      startOverPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      isReportStep={true}
    >
      <ParametersSectionTitle />
      <EditEntityValues
        selectedReportId={report.id}
        reportEntityMap={reportEntityMap}
      />
    </DeliverableStepContainer>
  );
};

export const MwEntityConfiguration = () => {
  const mwReportLaunchSeq = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.mwPrefillLaunchSeq
  );

  return <MwEntityConfigurationContent key={mwReportLaunchSeq} />;
};
